import React, { PropsWithChildren, useCallback, useContext } from 'react'
import { authService, formsAppService } from '@oneblink/apps-react'
import { useLoadDataState } from '@oneblink/apps-react'
import config from '../config'
import Loading from '../components/LoadingSpinner'

const AppConfigContext = React.createContext<
  formsAppService.FormsAppConfigurationResponse | undefined
>(undefined)

export function useAppConfig() {
  const appConfig = useContext(AppConfigContext)
  if (!appConfig) {
    throw new Error('AppConfigContext was used before it was set')
  }
  return {
    ...appConfig,
    ...appConfig.styles,
    formsOAuthClientId: appConfig.formsOAuthClientId || undefined,
  }
}

const FetchAppConfigError = () => {
  return <span>There has been an error fetching app configuration.</span>
}
FetchAppConfigError.displayName = 'FetchFormsError'

export default function AppConfigProvider({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  const loadAppConfig = useCallback(async () => {
    const response = await formsAppService.getFormsAppConfiguration(
      config.FORMS_APP_ID,
    )
    if (!response.formsOAuthClientId) {
      throw new Error('Unable to fetch app configuration')
    }

    authService.init({ oAuthClientId: response.formsOAuthClientId })
    return response
  }, [])

  const [state] = useLoadDataState(loadAppConfig)

  switch (state.status) {
    case 'ERROR': {
      return <FetchAppConfigError />
    }
    case 'LOADING': {
      return <Loading>Loading application</Loading>
    }
    case 'SUCCESS':
    default: {
      return (
        <AppConfigContext.Provider value={state.result}>
          {children}
        </AppConfigContext.Provider>
      )
    }
  }
}
